import { getTournamentImageUrl } from '../constants/config';

export const extractList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

export const extractActions = (data) => {
  if (Array.isArray(data?.actions)) return data.actions;
  if (Array.isArray(data?.data?.actions)) return data.data.actions;
  return extractList(data);
};

export const extractEntity = (data) => {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    if (data.data && typeof data.data === 'object' && !Array.isArray(data.data) && data.data.id) {
      return data.data;
    }
    return data;
  }
  return {};
};

export const isEnabled = (value) => Number(value) === 1;

export const toDateInputValue = (value) => {
  if (!value) return '';
  const str = String(value).trim();
  const dotted = str.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
  if (dotted) return `${dotted[3]}-${dotted[2]}-${dotted[1]}`;
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const date = new Date(str);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const fromDateInputValue = (value) => {
  if (!value) return '';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(value).trim();
  return `${match[3]}.${match[2]}.${match[1]}`;
};

export const toDateTimeLocalValue = (value) => {
  if (!value) return '';
  // seasons.startingAt/endingAt are MySQL DATETIME (wall-clock). API often
  // serializes them as ISO with Z — take Y-M-D H:M as-is, do not shift TZ.
  const str = String(value).trim();
  const localMatch = str.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
  if (localMatch) {
    return `${localMatch[1]}T${localMatch[2]}`;
  }
  const date = new Date(str);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const fromDateTimeLocalValue = (value) => {
  if (!value) return '';
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  if (!match) return String(value).trim();
  return `${match[1]} ${match[2]}:00`;
};

export const curatorDisplayName = (item) => (
  [item?.name, item?.family].filter(Boolean).join(' ').trim()
);

export const curatorInfoText = (item) => {
  const links = [
    item?.socialVk,
    item?.socialTg,
    item?.socialTel,
  ].filter(Boolean);
  return {
    position: item?.position || '',
    links,
  };
};

export const withCacheBust = (image) => {
  const url = getTournamentImageUrl(image);
  if (!url) return '';
  return `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`;
};

export const champFields = (champId) => {
  const id = Number(champId);
  if (!Number.isFinite(id) || id <= 0) return {};
  return { championat_id: id, champ_id: id };
};

export const applicationOptionLabel = (item) => {
  const name = item?.application_name || item?.name || '';
  const year = item?.year ? ` (${item.year})` : '';
  return `${name}${year}`.trim();
};

export const countryFields = (countryId) => {
  const id = Number(countryId);
  if (!Number.isFinite(id) || id <= 0) return {};
  return { country_id: id, county_id: id };
};

export const teamShowEnabled = (team) => {
  if (team?.enabled != null) return Number(team.enabled) === 1;
  if (team?.show != null) return Number(team.show) === 1;
  return true;
};

export const teamCountryId = (team) => {
  const id = Number(team?.country_id ?? team?.county_id ?? team?.countryId);
  return Number.isFinite(id) && id > 0 ? id : '';
};

export const coachDisplayName = (item) => (
  [item?.family, item?.name, item?.father_name].filter(Boolean).join(' ').trim()
);

export const playerDisplayName = (item) => (
  [item?.name, item?.family].filter(Boolean).join(' ').trim()
);

export const extractCoaches = (team) => extractList(
  team?.coaches
  || team?.officials
  || team?.official
  || team?.data?.coaches
);

export const coachRole = (item) => item?.description || item?.position || item?.role || '';

export const extractApplications = (payload) => {
  if (Array.isArray(payload?.applications)) return payload.applications;
  if (Array.isArray(payload?.data?.applications)) return payload.data.applications;
  if (Array.isArray(payload) && payload.some((item) => item && (item.application_id || item.players))) {
    return payload;
  }
  return [];
};

export const extractTeamPlayers = (payload) => {
  const applications = extractApplications(payload);
  const players = [];
  applications.forEach((app) => {
    extractList(app?.players).forEach((player) => {
      players.push({
        ...player,
        id: player.id || player.player_id,
        application_name: app.application_name || app.name || '',
      });
    });
  });
  return players;
};

export const coachBirthdayValue = (coach) => (
  coach?.birthdayDate || coach?.birthday || coach?.birth_date || coach?.birthdate || ''
);

export const normalizeSeasonTeam = (team) => ({
  team_id: Number(team.team_id !== undefined ? team.team_id : team.id),
  name: team.name || team.team_name || 'Без названия',
  image: team.image || '',
  short_name: team.short_name || '',
});

export const extractTeamLeagues = (team) => (
  extractList(team?.leagues).map((item) => ({
    league_id: Number(item.league_id ?? item.id),
    league_name: item.league_name || item.name || '',
    league_image: item.league_image || item.image || '',
    season_id: Number(item.season_id),
    season_name: item.season_name || item.season || '',
    country_id: item.country_id ? Number(item.country_id) : null,
  })).filter((item) => item.league_id && item.season_id)
);

const toTeamLinkId = (value) => {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
};

export const normalizeTeamLinkCard = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const id = toTeamLinkId(raw.id ?? raw.team_id);
  if (!id) return null;
  return {
    id,
    name: raw.name || raw.team_name || '',
    short_name: raw.short_name || '',
    image: raw.image || raw.team_image || '',
    championat_id: toTeamLinkId(raw.championat_id ?? raw.champ_id),
    parentTeam_id: toTeamLinkId(raw.parentTeam_id ?? raw.parent_team_id),
    reserveTeam_id: toTeamLinkId(raw.reserveTeam_id ?? raw.reserve_team_id),
  };
};

/** parent / reserve / chain из getAdminTeam или ответа set/deleteAdminTeamDouble. */
export const extractTeamDoubles = (payload = {}) => {
  const root = payload && typeof payload === 'object'
    ? (payload.doubles && typeof payload.doubles === 'object' ? payload.doubles : payload)
    : {};
  const team = normalizeTeamLinkCard(root.team) || normalizeTeamLinkCard(root);
  const parent = normalizeTeamLinkCard(root.parent)
    || (team?.parentTeam_id ? normalizeTeamLinkCard({ id: team.parentTeam_id }) : null);
  const reserve = normalizeTeamLinkCard(root.reserve)
    || (team?.reserveTeam_id ? normalizeTeamLinkCard({ id: team.reserveTeam_id }) : null);
  const chain = extractList(root.chain).map(normalizeTeamLinkCard).filter(Boolean);
  return {
    team,
    parent,
    reserve,
    chain: chain.length
      ? chain
      : [parent, team, reserve].filter(Boolean),
    parentTeam_id: parent?.id || team?.parentTeam_id || null,
    reserveTeam_id: reserve?.id || team?.reserveTeam_id || null,
  };
};

export const formatTeamDoubleConflicts = (conflicts, t) => {
  const list = Array.isArray(conflicts) ? conflicts : [];
  if (!list.length) return t('tmDoubleConflictGeneric');
  return list.map((item) => {
    const code = String(item?.code || '');
    if (code === 'main_has_reserve') {
      return t('tmDoubleConflictMainHasReserve', { id: item.reserveTeam_id || '—' });
    }
    if (code === 'double_has_parent') {
      return t('tmDoubleConflictDoubleHasParent', { id: item.parentTeam_id || '—' });
    }
    if (code === 'double_is_reserve_of_other') {
      const names = extractList(item.teams).map((row) => row.name || `#${row.id}`).filter(Boolean);
      return t('tmDoubleConflictDoubleIsReserve', { names: names.join(', ') || '—' });
    }
    if (code === 'main_has_other_child') {
      const names = extractList(item.teams).map((row) => row.name || `#${row.id}`).filter(Boolean);
      return t('tmDoubleConflictMainHasChild', { names: names.join(', ') || '—' });
    }
    return item?.err || code || t('tmDoubleConflictGeneric');
  }).join('\n');
};

export const seasonRosterTeamIds = (data) => {
  const list = Array.isArray(data?.teams) ? data.teams : extractList(data);
  return list
    .map((item) => Number(item.team_id ?? item.id))
    .filter((id) => Number.isFinite(id) && id > 0);
};

export const adminTeamsQuery = (champId, { search = '', limit = 40, offset = 0 } = {}) => ({
  ...champFields(champId),
  search: String(search || '').trim(),
  limit,
  offset,
});

/** Object keys for integer team ids are always strings in JS (`obj[5] === obj['5']`). */
const assignmentKey = (teamId) => {
  const id = Number(teamId);
  return Number.isFinite(id) && id > 0 ? String(id) : '';
};

/** Normalize assignment value → unique positive group ids (multi-group aware). */
export const teamGroupIds = (assignment, teamId) => {
  if (!assignment || teamId == null || teamId === '') return [];
  const key = assignmentKey(teamId);
  const raw = key
    ? (assignment[key] ?? assignment[Number(key)] ?? assignment[teamId])
    : assignment[teamId];
  const list = Array.isArray(raw) ? raw : (raw != null && raw !== '' ? [raw] : []);
  const seen = new Set();
  const ids = [];
  list.forEach((value) => {
    const id = Number(value);
    if (!Number.isFinite(id) || id <= 0 || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });
  return ids;
};

export const teamInGroup = (assignment, teamId, groupId) => (
  teamGroupIds(assignment, teamId).includes(Number(groupId))
);

export const setTeamGroupIds = (assignment, teamId, groupIds) => {
  const key = assignmentKey(teamId);
  if (!key) return { ...(assignment || {}) };
  const next = { ...(assignment || {}) };
  const ids = teamGroupIds({ [key]: groupIds }, key);
  if (!ids.length) delete next[key];
  else next[key] = ids;
  return next;
};

export const toggleTeamGroup = (assignment, teamId, groupId) => {
  const gid = Number(groupId);
  if (!Number.isFinite(gid) || gid <= 0) return assignment || {};
  const current = teamGroupIds(assignment, teamId);
  const nextIds = current.includes(gid)
    ? current.filter((id) => id !== gid)
    : [...current, gid];
  return setTeamGroupIds(assignment, teamId, nextIds);
};

export const removeTeamFromGroup = (assignment, teamId, groupId) => {
  const gid = Number(groupId);
  return setTeamGroupIds(
    assignment,
    teamId,
    teamGroupIds(assignment, teamId).filter((id) => id !== gid)
  );
};

export const deriveGroupsFromServer = (data) => {
  const raw = Array.isArray(data)
    ? data
    : (Array.isArray(data?.groups) ? data.groups : (Array.isArray(data?.data) ? data.data : []));
  if (!raw.length) return { list: [], assignment: {} };

  const looksStructured = Array.isArray(raw[0]?.teams)
    || ((raw[0]?.name || raw[0]?.group_name) && raw[0]?.team_id == null);

  const addMembership = (assignment, teamId, groupId) => {
    const tid = Number(teamId);
    const gid = Number(groupId);
    if (!Number.isFinite(tid) || tid <= 0 || !Number.isFinite(gid) || gid <= 0) return;
    const prev = teamGroupIds(assignment, tid);
    if (prev.includes(gid)) return;
    assignment[tid] = [...prev, gid];
  };

  if (looksStructured) {
    const list = [];
    const assignment = {};
    raw.forEach((group, index) => {
      const id = Number(group.id ?? group.group_id ?? index + 1);
      list.push({
        id,
        serverId: group.id ?? group.group_id ?? null,
        name: group.name || group.group_name || '',
        priority: Number(group.priority) || index + 1,
      });
      (group.teams || []).forEach((team) => {
        addMembership(assignment, team.team_id ?? team.id, id);
      });
    });
    return { list, assignment };
  }

  const list = [];
  const assignment = {};
  const idByName = {};
  raw.forEach((row) => {
    const name = row.group_name || row.name;
    if (!name) return;
    if (idByName[name] === undefined) {
      const id = Number(row.group_id ?? row.id ?? list.length + 1);
      idByName[name] = id;
      list.push({
        id,
        serverId: row.group_id ?? row.id ?? null,
        name,
        priority: Number(row.priority) || list.length + 1,
      });
    }
    addMembership(assignment, row.team_id, idByName[name]);
  });
  return { list, assignment };
};

export const DEFAULT_TABLE_TIEBREAKERS = [
  'h2h_points',
  'h2h_goal_diff',
  'h2h_goals_for',
  'h2h_wins',
  'goal_diff',
  'goals_for',
  'wins',
];

export const DEFAULT_TABLE_TIEBREAKER_PRESETS = {
  1: [...DEFAULT_TABLE_TIEBREAKERS],
  2: [
    'goal_diff',
    'goals_for',
    'wins',
    'h2h_points',
    'h2h_goal_diff',
    'h2h_goals_for',
    'h2h_wins',
  ],
};

const asCodeList = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item === 'object') {
        return String(item.code || item.id || item.key || '').trim();
      }
      return String(item || '').trim();
    }).filter(Boolean);
  }
  if (typeof value === 'string' && value.trim()) {
    return value.split(',').map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

export const normalizeTableTiebreakers = (value) => {
  const list = asCodeList(value);
  return list.length ? list : null;
};

export const parseTableTiebreakersDictionary = (data) => {
  const extracted = extractEntity(data);
  const root = (
    extracted
    && typeof extracted === 'object'
    && !extracted.criteria
    && !extracted.codes
    && !extracted.presets
    && extracted.data
    && typeof extracted.data === 'object'
    && !Array.isArray(extracted.data)
  ) ? extracted.data : extracted;
  const source = root.criteria || root.codes || root.items || root.list || root;
  let criteria = [];

  if (Array.isArray(source)) {
    criteria = source.map((item, index) => {
      if (typeof item === 'string') {
        return { code: item, name: '', priority: index + 1 };
      }
      const code = String(item.code || item.id || item.key || '').trim();
      if (!code) return null;
      return {
        code,
        name: item.name || item.title || item.label || '',
        priority: Number(item.priority) || index + 1,
      };
    }).filter(Boolean);
  } else if (source && typeof source === 'object') {
    criteria = Object.keys(source).map((code, index) => {
      const value = source[code];
      if (typeof value === 'string') {
        return { code, name: value, priority: index + 1 };
      }
      return {
        code,
        name: value?.name || value?.title || value?.label || '',
        priority: Number(value?.priority) || index + 1,
      };
    });
  }

  if (!criteria.length) {
    criteria = DEFAULT_TABLE_TIEBREAKERS.map((code, index) => ({
      code,
      name: '',
      priority: index + 1,
    }));
  }

  const presetsRaw = root.presets || root.preset || {};
  const presets = {
    1: asCodeList(presetsRaw[1] || presetsRaw['1'] || root.default || root.default_order),
    2: asCodeList(presetsRaw[2] || presetsRaw['2']),
  };
  if (!presets[1].length) presets[1] = [...DEFAULT_TABLE_TIEBREAKER_PRESETS[1]];
  if (!presets[2].length) presets[2] = [...DEFAULT_TABLE_TIEBREAKER_PRESETS[2]];

  const known = new Set(criteria.map((item) => item.code));
  [...presets[1], ...presets[2]].forEach((code) => {
    if (!known.has(code)) {
      known.add(code);
      criteria.push({ code, name: '', priority: criteria.length + 1 });
    }
  });

  return { criteria, presets };
};

export const buildTiebreakerItems = (criteria, enabledOrder) => {
  const order = asCodeList(enabledOrder);
  const byCode = new Map((criteria || []).map((item) => [item.code, item]));
  const used = new Set();
  const items = [];

  order.forEach((code) => {
    if (used.has(code)) return;
    used.add(code);
    const meta = byCode.get(code) || { code, name: '' };
    items.push({ code, name: meta.name || '', enabled: true });
  });

  (criteria || []).forEach((meta) => {
    if (used.has(meta.code)) return;
    used.add(meta.code);
    items.push({ code: meta.code, name: meta.name || '', enabled: false });
  });

  return items;
};

export const enabledTiebreakerCodes = (items) => (
  (items || []).filter((item) => item.enabled).map((item) => item.code)
);

export const sameTiebreakerOrder = (a, b) => (
  JSON.stringify(asCodeList(a)) === JSON.stringify(asCodeList(b))
);
